"use client";

import {
    createContext,
    useContext,
    ReactNode,
    useState,
    type Dispatch,
    type SetStateAction,
} from "react";
import type { Invite, InviteWithId } from "../../actions";

interface InvitesContextType {
    invites: Invite[];
    setInvites: Dispatch<SetStateAction<Invite[]>>;
    preapproved: Invite[];
    setPreapproved: Dispatch<SetStateAction<Invite[]>>;
    proximity: InviteWithId[];
    setProximity: Dispatch<SetStateAction<InviteWithId[]>>;
    selectedFollowers: InviteWithId[];
    setSelectedFollowers: Dispatch<SetStateAction<InviteWithId[]>>;
    message: string;
    setMessage: Dispatch<SetStateAction<string>>;
    subject: string;
    setSubject: Dispatch<SetStateAction<string>>;
    from: string;
    setFrom: Dispatch<SetStateAction<string>>;
    totalCount: {
        preapproved: number;
        proximity: number;
        followers: number;
        invites: number;
    };
    setTotalCount: Dispatch<
        SetStateAction<{
            preapproved: number;
            proximity: number;
            followers: number;
            invites: number;
        }>
    >;
    clearInvitesState: () => void;
}

const InvitesContext = createContext<InvitesContextType | undefined>(undefined);

export function InvitesProvider({ children }: { children: ReactNode }) {
    const [invites, setInvites] = useState<Invite[]>([]);
    const [preapproved, setPreapproved] = useState<Invite[]>([]);
    const [proximity, setProximity] = useState<InviteWithId[]>([]);
    const [selectedFollowers, setSelectedFollowers] = useState<InviteWithId[]>(
        [],
    );
    const [totalCount, setTotalCount] = useState({
        preapproved: 0,
        proximity: 0,
        followers: 0,
        invites: 0,
    });
    const [message, setMessage] = useState("");
    const [subject, setSubject] = useState("");
    const [from, setFrom] = useState("");
    const clearInvitesState = () => {
        setInvites([]);
        setPreapproved([]);
        setProximity([]);
        setSelectedFollowers([]);
        setMessage("");
        setSubject("");
        setFrom("");
    };

    const value = {
        invites,
        setInvites,
        preapproved,
        setPreapproved,
        proximity,
        setProximity,
        selectedFollowers,
        setSelectedFollowers,
        message,
        setMessage,
        subject,
        setSubject,
        from,
        setFrom,
        clearInvitesState,
        totalCount,
        setTotalCount,
    };

    return (
        <InvitesContext.Provider value={value}>
            {children}
        </InvitesContext.Provider>
    );
}

export function useInvites() {
    const context = useContext(InvitesContext);
    if (context === undefined) {
        throw new Error("useInvites must be used within an InvitesProvider");
    }
    return context;
}
